import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import {
  cloneDeep,
  filter,
  flattenDeep,
  groupBy,
  isEqual,
  isNil,
  keys,
  pick,
  uniqBy,
} from 'lodash';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import { titleize, underscored } from 'underscore.string';
import moment from 'moment';
import AccessorialActionCreators from 'actions/AccessorialRequestActionCreators';
import Alert from 'components/ui/Alert';
import BulkAddAccessorialsStore from 'stores/BulkAddAccessorialsStore';
import ToastStore from 'stores/ToastStore';
import { Disclosure } from '@headlessui/react';
import { ChevronDown, ChevronRight, Lightbulb, Loader2, X } from 'lucide-react';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import {
  getLaneAccessorialRatesV2,
  getAccessorialRequestsWithStopDetails,
} from 'utils/AccessorialRequestWebAPIUtils';
import { getShipmentsWithDetails } from 'utils/ShipmentWebAPIUtils';
import {
  getAppliesToFields,
  getAutoUnitCalculation,
} from 'utils/accessorials/unitCalculation';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  mapProcessedAccessorialLedgerItem,
  checkWhichSideAccessorialAutoDeclined,
  isCarrierMissingInAccessorial,
  getCarrierAccordionTitle,
} from 'helpers/accessorials';
import DrayageAccessorialRow from './DrayageAccessorialRow';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { sluggify } from 'utils/localization/utils';
import ShipmentLink from 'components/modules/carrier/ShipmentLink';
import ErrorAlert from 'components/ui/ErrorAlert';

const ALERT_MESSAGES = {
  carrier_mismatch: 'ACCESSORIALS_WITH_CARRIER_MISMATCH',
  carrier_missing: 'ACCESSORIALS_WITH_CARRIER_MISSING',
  carrier_mismatch_and_missing:
    'ACCESSORIALS_WITH_CARRIER_MISMATCH_AND_MISSING',
  auto_applied_shipper_change_edit: 'AUTO_APPLIED_SHIPPER_CHANGE_EDIT',
};

const META_MAPPINGS = {
  NAME: 'name',
  TYPE: 'type',
  EQUIPMENT: 'equipment',
  SHIPPER_COST: 'shipper_cost',
  CARRIER_COST: 'carrier_cost',
  SHIPPER_UNIT_QTY: 'shipper_unit_qty',
  CARRIER_UNIT_QTY: 'carrier_unit_qty',
  VENDOR: 'vendor',
  NOTES: 'notes',
  DESCRIPTION: 'description',
  CHARGE_CODE: 'charge_code',
  BEGIN_DATETIME: 'begin_datetime',
  END_DATETIME: 'end_datetime',
  LOCATION: 'location',
  REQUIRED_FIELDS: 'required_fields',
};

const REQUIRED_FIELDS_MAP = {
  begin_date_required: 'begin_datetime',
  end_date_required: 'end_datetime',
  location_required: 'location',
  description_required: 'description',
};

const EDIT_FIELDS_MAP = {
  shipper_begin_datetime: { shipper: 'shipper_begin_datetime' },
  begin_datetime: { carrier: 'begin_datetime' },
  shipper_end_datetime: { shipper: 'shipper_end_datetime' },
  end_datetime: { carrier: 'end_datetime' },
  shipper_location: { shipper: 'shipper_location' },
  location: { carrier: 'location' },
  shipper_description: { shipper: 'shipper_description' },
  description: { carrier: 'description' },
  shipper_note: { shipper: 'shipper_note' },
  carrier_note: { carrier: 'carrier_note' },
  carrier_unit_qty: { carrier: 'carrier_unit_qty' },
  shipper_unit_qty: { shipper: 'shipper_unit_qty' },
  shipper_cost: { shipper: 'shipper_rate' },
  carrier_cost: { carrier: 'carrier_rate' },
};

const EDIT_COST_KEY_MAPPINGS = {
  carrier_cost: 'carrier_rate',
  shipper_cost: 'shipper_rate',
};

const MAX_SHIPMENTS_LIMIT = 100;

// Rows can share shipment_id/_id (multiple accessorials per shipment), so we
// stamp each row with a stable synthetic id to use as its React key. Index keys
// cause stateful rows to delete the wrong row on removal.
let rowIdSeq = 0;
const nextRowId = () => {
  rowIdSeq += 1;
  return `row-${rowIdSeq}`;
};

class BulkAddAccessorialsModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    edit: PropTypes.bool,
    shipmentIds: PropTypes.array,
    selectedAccessorials: PropTypes.array,
    onHide: PropTypes.func.isRequired,
  };

  static defaultProps = {
    shipments: [],
    selectedAccessorials: [],
    edit: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      saveInProgress: false,
      shipments: [],
      proTipVisible: true,
      status: [],
      loadingShipments: false,
      accessorialRequiredDocsReturned: false,
      accessorialsToEdit: [],
      customerApprovalAccessorials: [],
      customerApprovalProvided: false,
      allEquipments: [],
      shipmentsLimitError: false,
    };
  }

  componentDidMount() {
    BulkAddAccessorialsStore.addChangeListener(this.handleStoreChange);
    this.getInitData();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.show && nextProps.show) {
      const comparisonResults = compareShipments(
        this.props.shipments,
        nextProps.shipments,
      );

      const visibilityChanged = this.props.show !== nextProps.show;
      if (comparisonResults.modified || visibilityChanged) {
        this.mapStateToProps(comparisonResults.shipments);
      }
    }
  }

  componentWillUnmount() {
    BulkAddAccessorialsStore.removeChangeListener(this.handleStoreChange);
  }

  async getInitData() {
    const { edit, selectedAccessorials, shipmentIds, t } = this.props;

    if (shipmentIds.length > MAX_SHIPMENTS_LIMIT) {
      this.setState({ proTipVisible: false, shipmentsLimitError: true });
      return;
    }

    this.setState({ loadingShipments: true }, async () => {
      try {
        await this.fetchAccessorials();
        if (edit && selectedAccessorials.length) {
          const accessorialsData =
            await this.getAccessorialRequestsWithStopDetails(
              shipmentIds,
              selectedAccessorials,
            );
          this.setDrayageAccessorialRowsForEdit(accessorialsData);
        } else {
          const result = await getShipmentsWithDetails({
            _id: shipmentIds,
            current: [true, false],
          });
          if (result?.entity?.success) {
            const shipments = result?.entity?.jobs?.items || [];
            if (shipments?.length) {
              this.mapStateToProps(shipments);
            }
          } else {
            throw new Error('PROBLEM_FETCHING_DATA');
          }
        }
      } catch (e) {
        ToastStore.addNotification({
          title: t('PROBLEM_FETCHING_DATA'),
          message: e?.message || e,
          level: 'error',
        });
      }
      this.setState({ loadingShipments: false });
    });
  }

  async getLaneAccessorialRates(query, type, t) {
    const queryString = new URLSearchParams(query).toString();
    try {
      const results = await getLaneAccessorialRatesV2(queryString);
      const {
        data: { laneAccessorialRates },
      } = results;

      if (!laneAccessorialRates) {
        return null;
      }

      const rate = laneAccessorialRates
        .filter((acc) => acc.accessorialtype)
        .find(
          (r) =>
            r.accessorialtype === type ||
            r.accessorialtype.toLowerCase() === type,
        );

      return rate;
    } catch (e) {
      ToastStore.addNotification({
        title: t('PROBLEM_FETCHING_COSTS'),
        message: e,
        level: 'error',
      });
      return null;
    }
  }

  async getAccessorialRequestsWithStopDetails(shipmentIds, accessorialIds) {
    const { t } = this.props;
    try {
      const query = { shipment: shipmentIds, id: accessorialIds };
      const results = await getAccessorialRequestsWithStopDetails(query);
      return results;
    } catch (e) {
      ToastStore.addNotification({
        title: t('LOADING_ACCESSORIALS_ERROR'),
        body: '',
        level: 'error',
      });
      throw e;
    }
  }

  fetchAccessorialRequestRate = async (rowId, self) => {
    const { t } = self.props;
    const shipment = self.state.shipments.find((s) => s._rowId === rowId);
    if (!shipment) return;
    const { carrier, shipper, meta: currentMeta, market } = shipment;
    const { name } = currentMeta;

    let updates = {
      ratesRequestPending: false,
    };

    const query = {
      customer: shipper._id,
      customerType: 'shipper',
      market: market.name,
      status: 'confirmed',
    };

    const shipperRate = await self.getLaneAccessorialRates(query, name, t);

    if (shipperRate && shipperRate.ratetype) {
      if (shipperRate.ratetype === '$') {
        updates.shipper_cost = shipperRate.rate;
        updates.receivable_rate_source = 'automated';
      } else if (shipperRate.ratetype === '%' && shipment.receivables) {
        const lineHaulShipper = shipment.receivables.reduce((acc, item) => {
          return item.category === 'base_cost' ? item.value + acc : acc;
        }, 0);
        if (lineHaulShipper > 0 && parseFloat(lineHaulShipper)) {
          updates.shipper_cost = parseFloat(
            (lineHaulShipper * (shipperRate.rate / 100)).toFixed(2),
          );
          updates.receivable_rate_source = 'automated';
        }
      }
    } else {
      updates.shipper_cost = 0;
    }

    if (carrier) {
      const carrierQuery = {
        customer: carrier._id,
        customerType: 'carrier',
        market: market.name,
        status: 'confirmed',
      };
      const carrierRate = await self.getLaneAccessorialRates(
        carrierQuery,
        name,
        t,
      );

      if (carrierRate && carrierRate.ratetype) {
        if (carrierRate.ratetype === '$') {
          updates.carrier_cost = carrierRate.rate;
          updates.payable_rate_source = 'automated';
        } else if (carrierRate.ratetype === '%' && shipment.payables) {
          const lineHaulCarrier = shipment.payables.reduce((acc, item) => {
            return item.category === 'base_cost' ? item.value + acc : acc;
          }, 0);

          if (lineHaulCarrier > 0 && parseFloat(lineHaulCarrier)) {
            updates.carrier_cost = parseFloat(
              (lineHaulCarrier * (carrierRate.rate / 100)).toFixed(2),
            );
            updates.payable_rate_source = 'automated';
          }
        }
      } else {
        updates.carrier_cost = 0;
      }
    }

    self.setState((prevState) => {
      const { shipments: currentShipments } = prevState;
      // Re-resolve by _rowId: the row may have been removed/reordered while the
      // rate request was in flight. Splicing by a captured index would reinsert
      // the stale (deleted) row and clobber whichever row now holds that slot.
      const targetIndex = currentShipments.findIndex((s) => s._rowId === rowId);
      if (targetIndex === -1) {
        return null;
      }
      const target = currentShipments[targetIndex];
      const updatedShipment = {
        ...target,
        meta: { ...target.meta, ...updates },
      };
      return {
        shipments: [
          ...currentShipments.slice(0, targetIndex),
          updatedShipment,
          ...currentShipments.slice(targetIndex + 1),
        ],
      };
    });
  };

  setDrayageAccessorialRowsForEdit = (accessorialsToEdit) => {
    const shipments = accessorialsToEdit.map((acc) => ({
      ...mapShipmentForEdit(acc, this.state.accessorials),
      _rowId: nextRowId(),
    }));
    this.setState({
      shipments,
      accessorialsToEdit,
    });
  };

  fetchAccessorials = () => {
    const { t } = this.props;
    return new Promise((resolve) => {
      this.setState({ loadingAccessorials: true }, async () => {
        try {
          const result = await getAccessorials({
            paginate: false,
          });

          const {
            entity: { items },
          } = result;

          const accessorials = items.map((a) => ({
            ...a,
            slug: a.slug || underscored(a.name.toLowerCase()),
          }));

          this.setState(
            {
              accessorials,
              loadingAccessorials: false,
            },
            () => resolve(),
          );
        } catch (e) {
          ToastStore.addNotification({
            title: t('LOADING_ACCESSORIALS_ERROR'),
            body: `${e.message}`,
            level: 'error',
          });
        }
      });
    });
  };

  handleCloseStatus = (type) => () => {
    this.setState((currentState) => {
      const { status: currentStatusList } = currentState;

      const status = currentStatusList.filter((s) => s.type !== type);
      return {
        status,
      };
    });
  };

  handleCloseTip = (e) => {
    e.preventDefault();
    this.setState({
      proTipVisible: false,
    });
  };

  handleClearAll = () => {
    this.setState((currentState) => {
      const { shipments: currentShipments } = currentState;

      const shipments = currentShipments.map((job) => ({
        ...job,
        meta: getDefaultMeta(),
      }));

      return {
        shipments,
      };
    });
  };

  updateRateSource = (meta, field) => {
    if (!field.includes('cost')) {
      return meta;
    }

    meta.rate_source = 'manual';

    if (field === 'carrier_cost') {
      meta.payable_rate_source =
        meta?.payable_rate_source === 'automated' ? 'overwritten' : 'manual';
    }
    if (field === 'shipper_cost') {
      meta.receivable_rate_source =
        meta?.receivable_rate_source === 'automated' ? 'overwritten' : 'manual';
    }
    return meta;
  };

  handleUpdateAccessorialRequiredDocs = (bool) => {
    this.setState({
      accessorialRequiredDocsReturned: bool,
    });
  };

  handleFieldChange = (index, handleUpdateShipment) => (field, value) => {
    const { shipments } = this.state;
    const currentShipment = shipments[index];
    const currentFieldValue = currentShipment?.meta[field];
    if (value != currentFieldValue) {
      handleUpdateShipment(index, field, value);
    }
  };

  handleUpdateShipment = (index, field, value) => {
    try {
      const rateDependencies = ['name'];
      this.setState(
        (prevState) => {
          const { shipments: currentShipments } = prevState;

          if (index === -1) {
            return {
              shipments: [...currentShipments],
            };
          }
          const shipment = cloneDeep(currentShipments[index]);
          const isRateDependent = rateDependencies.includes(field);
          shipment.meta[field] = value;
          shipment.meta.ratesRequestPending = isRateDependent;
          shipment.meta = this.updateRateSource(shipment.meta, field);
          return {
            fetchAccessorialRates: isRateDependent,
            shipments: [
              ...currentShipments.slice(0, index),
              shipment,
              ...currentShipments.slice(index + 1),
            ],
          };
        },
        () => {
          if (this.state.fetchAccessorialRates) {
            const rowId = this.state.shipments[index]?._rowId;
            this.fetchAccessorialRequestRate(rowId, this);
          }
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  handleUpdateCustomerApproval = (index) => (required, provided) => {
    this.setState((currentState) => {
      const { customerApprovalAccessorials, shipments } = currentState;
      const accessorialInfo = shipments[index]?.meta?.type;
      const currentCustomerApproval = cloneDeep(customerApprovalAccessorials);
      if (required) {
        if (!currentCustomerApproval.includes(accessorialInfo)) {
          currentCustomerApproval.push(accessorialInfo);
        }
      }

      return {
        customerApprovalAccessorials: currentCustomerApproval,
        customerApprovalProvided: provided,
      };
    });
  };

  handleVendorFieldChange =
    (index) =>
    ({ vendor_name, vendor }) => {
      this.setState((prevState) => {
        const { shipments: currentShipments } = prevState;

        if (index === -1) {
          return {
            shipments: [...currentShipments],
          };
        }

        const shipment = cloneDeep(currentShipments[index]);
        shipment.meta.vendor_name = vendor_name;
        shipment.meta.vendor = vendor;
        shipment.meta.ratesRequestPending = !vendor;

        return {
          shipments: [
            ...currentShipments.slice(0, index),
            shipment,
            ...currentShipments.slice(index + 1),
          ],
        };
      });
    };

  handleRemove = (index) => () => {
    this.setState((currentState) => {
      const { shipments: currentShipments } = currentState;

      return {
        shipments: [
          ...currentShipments.slice(0, index),
          ...currentShipments.slice(index + 1),
        ],
      };
    });
  };

  handleApplyAll = (index) => () => {
    this.setState((prevState) => {
      const { shipments: currentShipments } = prevState;

      const jobIndex = index;
      if (jobIndex === -1) {
        return {
          shipments: [...currentShipments],
        };
      }

      const shipment = cloneDeep(currentShipments[jobIndex]);
      const { meta: targetShipmentMeta } = shipment;

      const updatedShipments = currentShipments.map((job, i) => {
        // NOTE: don't apply before before the current.
        if (i < jobIndex) {
          return {
            ...job,
            meta: {
              ...job.meta,
              updated: true,
            },
          };
        }

        // current job meta data
        let meta = {
          ...job.meta,
        };

        /*
         * extract keys from meta date
         * whose value need to apply rest shipments meta data
         */

        const targetShipmentMetaKeys = Object.keys(targetShipmentMeta);

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.LOCATION)) {
          meta.location = targetShipmentMeta.location;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.BEGIN_DATETIME)) {
          meta.begin_datetime = targetShipmentMeta.begin_datetime;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.END_DATETIME)) {
          meta.end_datetime = targetShipmentMeta.end_datetime;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.REQUIRED_FIELDS)) {
          meta.required_fields = targetShipmentMeta.required_fields;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.NAME)) {
          meta.name = targetShipmentMeta.name;
          meta.hasCarrierRate = targetShipmentMeta.hasCarrierRate;
          meta.hasShipperRate = targetShipmentMeta.hasShipperRate;
          meta.ratesRequestPending = targetShipmentMeta.ratesRequestPending;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.DESCRIPTION)) {
          meta.description = targetShipmentMeta.description;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.TYPE)) {
          meta.type = targetShipmentMeta.type;
          meta.updated = true;
        }

        if (targetShipmentMeta.payee_type) {
          meta.payee_type = targetShipmentMeta.payee_type;
          meta.updated = true;
        }

        // add equipment to only same shipment.
        if (shipment?._id === job?._id) {
          meta.equipment = targetShipmentMeta.equipment;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.VENDOR)) {
          meta.vendor = targetShipmentMeta.vendor;
          meta.vendor_name = targetShipmentMeta.vendor_name;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.SHIPPER_COST)) {
          meta.shipper_cost = targetShipmentMeta.shipper_cost;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.CARRIER_COST)) {
          meta.carrier_cost = targetShipmentMeta.carrier_cost;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.SHIPPER_UNIT_QTY)) {
          meta.shipper_unit_qty = targetShipmentMeta.shipper_unit_qty;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.CARRIER_UNIT_QTY)) {
          meta.carrier_unit_qty = targetShipmentMeta.carrier_unit_qty;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.NOTES)) {
          meta.notes = targetShipmentMeta.notes;
          meta.updated = true;
        }

        if (targetShipmentMetaKeys.includes(META_MAPPINGS.CHARGE_CODE)) {
          meta.charge_code = targetShipmentMeta.charge_code;
          meta.updated = true;
        }
        return {
          ...job,
          meta,
        };
      });
      return {
        shipments: [...updatedShipments],
      };
    });
  };

  mapStateToProps(newShipments) {
    const shipments = newShipments.map((job) => ({
      ...mapShipment(job),
      _rowId: nextRowId(),
    }));

    this.setState({
      shipments,
      saveInProgress: false,
      proTipVisible: true,
      status: [],
    });
  }

  resetShipmentsMeta() {
    const { shipments } = this.state;

    return shipments.map((s) => {
      return {
        ...s,
        meta: getDefaultMeta(),
      };
    });
  }

  resetState() {
    this.setState({
      saveInProgress: false,
      shipments: [],
      proTipVisible: true,
    });
  }

  handleClose = () => {
    this.resetState();
    this.props.onHide();
  };

  handleSave = () => {
    const { edit } = this.props;
    let isValid = false;
    this.setState(
      (prevState) => {
        const updatedState = this.validate(prevState);
        isValid = updatedState.saveInProgress;
        return updatedState;
      },
      async () => {
        if (isValid && !edit) {
          this.handleCreateAccessorialRequests();
        }
        if (isValid && edit) {
          this.handleEditAccessorialRequests();
        }
      },
    );
  };

  handleCreateAccessorialRequests = () => {
    const {
      shipments,
      accessorials,
      customerApprovalAccessorials,
      customerApprovalProvided,
    } = this.state;
    const requests = shipments.map((shipment) =>
      convertToAccessorialRequest(
        shipment,
        accessorials,
        customerApprovalAccessorials,
        customerApprovalProvided,
      ),
    );
    AccessorialActionCreators.bulkCreateAccessorialRequests(requests);
  };

  getRateSource = (originalRateSource, original_rate, updated_rate) => {
    if (
      (original_rate && updated_rate && original_rate === updated_rate) ||
      originalRateSource === 'overwritten_price_engine'
    )
      return originalRateSource;
    if (originalRateSource === 'automated') {
      return 'overwritten_price_engine';
    }
    return 'manual';
  };

  handleEditAccessorialRequests = () => {
    const { shipments, accessorialsToEdit } = this.state;
    const requests = shipments
      .map((shipment) => {
        const originalAccessorial = accessorialsToEdit.find(
          (acc) => acc._id === shipment.meta._id,
        );
        const difference = filter(keys(shipment.meta), (k) => {
          /**
           * Why this cost_key check?
           * Existing code for accessorial row uses carrier_cost and shipper_cost as rate key
           */
          const cost_key = EDIT_COST_KEY_MAPPINGS[k];
          if (cost_key) {
            return !isEqual(originalAccessorial[cost_key], shipment.meta[k]);
          }
          return !isEqual(originalAccessorial[k], shipment.meta[k]);
        });
        const { disable_payer, disable_payee, notes } = shipment.meta;
        if (notes !== originalAccessorial.note) difference.push('notes');
        const validEditFields = pick(EDIT_FIELDS_MAP, difference);
        return keys(validEditFields).reduce((payload, field) => {
          payload._id = originalAccessorial._id;
          if (!disable_payer && validEditFields[field]?.shipper) {
            payload[validEditFields[field]?.shipper] = shipment.meta[field];
            payload['shipper_status'] = 'pending-shipper';
          }
          if (!disable_payee && validEditFields[field]?.carrier) {
            payload[validEditFields[field]?.carrier] = shipment.meta[field];
            payload['carrier_status'] = 'pending-admin';
          }
          if (payload.carrier_rate || payload.shipper_rate) {
            payload['rate_source'] = this.getRateSource(
              originalAccessorial.rate_source,
            );
          }
          if (payload.carrier_rate) {
            payload['rate_source'] = this.getRateSource(
              originalAccessorial.rate_source,
              originalAccessorial.carrier_rate,
              payload.carrier_rate,
            );
            payload['payable_rate_source'] = this.getRateSource(
              originalAccessorial.payable_rate_source,
              originalAccessorial.carrier_rate,
              payload.carrier_rate,
            );
          }
          if (payload.shipper_rate) {
            payload['rate_source'] = this.getRateSource(
              originalAccessorial.rate_source,
              originalAccessorial.shipper_rate,
              payload.shipper_rate,
            );
            payload['receivable_rate_source'] = this.getRateSource(
              originalAccessorial.receivable_rate_source,
              originalAccessorial.shipper_rate,
              payload.shipper_rate,
            );
          }
          return payload;
        }, {});
      })
      .filter((item) => item._id);

    const submitAndClose = async () => {
      await AccessorialActionCreators.updateAccessorialException(requests);
      this.handleClose();
    };
    submitAndClose();
  };

  handleStoreChange = () => {
    const { t } = this.props;
    const result = BulkAddAccessorialsStore.getBulkAddResult();

    const { successes: successIds, errors } = result;
    this.setState((prevState) => {
      let { shipments, status } = prevState;

      if (successIds.length) {
        const items = shipments.map((s) => s.shipment_id);
        status.push({
          type: 'success',
          message: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
          items,
        });
      }

      if (successIds.length && !errors.length) {
        ToastStore.addNotification({
          title: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
          body: '',
          level: 'success',
        });
        this.handleClose();
      }

      if (errors) {
        const newStatus = [];
        const getShipmentId = (err) => {
          return shipments
            .filter((s) => s.meta.type === err.accessorial_type)
            .map((s) => s.shipment_id)[0];
        };

        errors.forEach((err) => {
          if (newStatus.some((item) => item.message === err.errMessage)) {
            const index = newStatus.findIndex(
              (i) => i.message === err.errMessage,
            );

            const shipmentId = getShipmentId(err);

            if (!newStatus[index].items.includes(shipmentId)) {
              newStatus[index].items.push(shipmentId);
            }
          } else {
            newStatus.push({
              type: 'error',
              message: err.errMessage || t('SHIPMENTS_UPDATE_FAILURE_TIP'),
              items: [getShipmentId(err)],
            });
          }
        });

        status = status.concat(newStatus);
      }

      const updatedShipments =
        errors.length === 0 ? this.resetShipmentsMeta() : shipments;
      const statusToDisplay = this.transformStatusToDisplay(status);

      return {
        status: statusToDisplay,
        saveInProgress: false,
        shipments: updatedShipments,
      };
    });
  };

  transformStatusToDisplay(status) {
    const { t } = this.props;
    const initValue = [
      {
        type: 'success',
        message: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
        items: [],
      },
      {
        type: 'error',
        items: [],
      },
    ];

    return status
      .reduce((list, s) => {
        if (s.type === 'success') {
          const oldItems = list[0].items;
          if (!oldItems) {
            list[0].items = [...s.items];
          }
          list[0].items.push(s);
        }
        if (s.type === 'error') {
          const oldItems = list[1].items;
          if (!oldItems) {
            list[1].items = [...s.items];
          }
          list[1].items.push(s);
        }
        return list;
      }, initValue)
      .filter((el) => el.items.length);
  }
  validate(currentState) {
    const { shipments: currentShipments } = currentState;
    const { t } = this.props;
    let saveInProgress = false;
    const shipments = currentShipments.map((job) =>
      validateJob(job, t, this.state.accessorials),
    );

    saveInProgress = shipments.every((s) => s.meta.isValid);

    return {
      shipments,
      saveInProgress,
    };
  }

  renderShipmentsLimitErrorInfo() {
    const { t } = this.props;
    const { shipmentsLimitError } = this.state;
    if (!shipmentsLimitError) {
      return null;
    }
    return (
      <ErrorAlert title={t('UNABLE_PERFORM_BULK_ACTION')} canClose={false}>
        {`${t('ERROR')}: ${t('SHIPMENT_LIMIT_ERROR_INFO')}`}
      </ErrorAlert>
    );
  }

  renderProTip() {
    const { t } = this.props;
    if (!this.state.proTipVisible) {
      return null;
    }
    return (
      <div className="flex items-center justify-between gap-3 w-full px-4 py-2 my-4 rounded-md bg-blue-lightest text-blue-dark">
        <div className="flex items-center gap-2 min-w-0">
          <Lightbulb className="h-5 w-5 shrink-0" />
          <span className="text-sm text-blue">
            {t(
              'BULK_ACCESSORIALS_PRO_TIP',
              'Add a row per accessorial charge, then expand a row for description, times, and documents. Accessorials can be added once a carrier is found.',
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={this.handleCloseTip}
          aria-label={titleize(t('DISMISS'))}
        >
          <X className="w-5 h-5" />
        </Button>
      </div>
    );
  }

  handleAddRow = (index) => {
    const { shipments } = this.state;
    const shipment = cloneDeep(shipments[index]);

    if (!shipment || !shipments) {
      return null;
    }
    shipment.meta = getDefaultMeta();
    shipment._rowId = nextRowId();
    shipments.push(shipment);

    // Put shipments in order
    const orderedShipments = flattenDeep(
      Object.values(groupBy(shipments, 'shipment_id')),
    );
    this.setState({ shipments: orderedShipments });
  };

  renderColumnHeaders() {
    const { t, edit } = this.props;
    return (
      <TableHeader>
        <TableRow>
          <TableHead aria-hidden="true" />
          <TableHead>{titleize(t('SHIPMENT', { count: 1 }))}</TableHead>
          <TableHead>{titleize(t('EQUIPMENT'))}</TableHead>
          <TableHead>{titleize(t('NAME_DESCRIPTION'))}</TableHead>
          <TableHead>{titleize(t('PAYEE_TYPE'))}</TableHead>
          <TableHead>{titleize(t('SHIPPER_RATE'))}</TableHead>
          <TableHead>{titleize(t('UNIT', { count: 2 }))}</TableHead>
          <TableHead>{titleize(t('TOTAL'))}</TableHead>
          <TableHead>{titleize(t('CARRIER_VENDOR_RATE'))}</TableHead>
          <TableHead>{titleize(t('UNIT', { count: 2 }))}</TableHead>
          <TableHead>{titleize(t('TOTAL'))}</TableHead>
          {!edit && <TableHead>{titleize(t('APPLY_TO_ALL'))}</TableHead>}
          <TableHead>{titleize(t('ACTIONS'))}</TableHead>
        </TableRow>
      </TableHeader>
    );
  }

  renderRows() {
    const { t, edit } = this.props;
    const {
      loadingAccessorials,
      loadingShipments,
      shipments,
      shipmentsLimitError,
    } = this.state;
    if (loadingAccessorials || loadingShipments) {
      const colCount = edit ? 12 : 13;
      return (
        <TableBody>
          {Array.from({ length: 3 }).map((_, rowIdx) => (
            <TableRow key={`skeleton-row-${rowIdx}`}>
              {Array.from({ length: colCount }).map((__, colIdx) => (
                <TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
                  <Skeleton className="h-8 w-full rounded-md" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      );
    }

    if (shipmentsLimitError) {
      return null;
    }

    if (shipments.length === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell>{t('NO_SHIPMENTS_AVAILABLE_TO_UPDATE')}</TableCell>
          </TableRow>
        </TableBody>
      );
    }
    return (
      <TableBody>
        {shipments.map((job, index) => {
          return (
            job.status !== 'job_canceled' && (
              <DrayageAccessorialRow
                index={index}
                shipment={job}
                accessorials={this.state.accessorials}
                onRemove={this.handleRemove(index)}
                onFieldChange={this.handleFieldChange(
                  index,
                  this.handleUpdateShipment,
                )}
                handleUpdateCustomerApproval={this.handleUpdateCustomerApproval(
                  index,
                )}
                onVendorFieldChange={this.handleVendorFieldChange(index)}
                onApplyAll={this.handleApplyAll(index)}
                onRowAdd={() => this.handleAddRow(index)}
                key={job._rowId ?? index}
                carrierEnabled={job.carrier}
                disabledApplyBtn={
                  shipments.length === 1 || index === shipments.length - 1
                }
                handleUpdateAccessorialRequiredDocs={
                  this.handleUpdateAccessorialRequiredDocs
                }
                edit={edit}
                defaultAccessorialType={this.props.defaultAccessorialType}
              />
            )
          );
        })}
      </TableBody>
    );
  }

  renderErrors() {
    const { shipments } = this.state;
    const { t } = this.props;
    const hasErrors = shipments.find((s) => Object.keys(s.meta.errors).length);
    if (!hasErrors) {
      return null;
    }

    const errors = shipments.reduce((runningList, job) => {
      return runningList.concat(...Object.values(job.meta.errors));
    }, []);

    return (
      <Alert type="error" title={t('CORRECT_ISSUES')}>
        <ul>
          {errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderStatus(status) {
    const { type, items } = status;

    return (
      <>
        {items.map((i) => (
          <Alert
            className="mb-1.5"
            type={type}
            key={type}
            title={i.message}
            canClose
            onClose={this.handleCloseStatus(type)}
          >
            {i.items.length && (
              <ul>
                {i.items.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            )}
          </Alert>
        ))}
      </>
    );
  }

  renderStatuses() {
    const { status } = this.state;

    if (!status || status.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col mr-4 stop-status-wrapper">
        {status.map((s) => this.renderStatus(s))}
      </div>
    );
  }

  disableSave = () => {
    const {
      saveInProgress,
      shipments,
      loadingAccessorials,
      accessorialRequiredDocsReturned,
    } = this.state;
    if (
      saveInProgress ||
      shipments.length === 0 ||
      loadingAccessorials ||
      (featureEnabled(CONFIGURED_FEATURES.SSR) &&
        !accessorialRequiredDocsReturned)
    ) {
      return true;
    }
    return false;
  };

  getAlertMessage(alertType) {
    return ALERT_MESSAGES[alertType] || '';
  }

  checkProcessedAccessorials(selectedAccessorials = [], ledger) {
    return selectedAccessorials.filter((selectedAccessorial) =>
      mapProcessedAccessorialLedgerItem(selectedAccessorial, ledger),
    );
  }

  getAccessorialProcessedStatus(accessorial, ledger) {
    const isInProcessedLedger = this.checkProcessedAccessorials(
      [accessorial],
      ledger,
    )?.length;
    if (isInProcessedLedger) {
      return ledger === 'payables' ? 'PAID' : 'INVOICED';
    }
    return ledger === 'payables'
      ? accessorial.carrier_status
      : accessorial.shipper_status;
  }

  renderMismatchAlerts(
    accessorials = [],
    shipperSideDeclinedAccessorial = false,
  ) {
    const { t } = this.props;
    return accessorials.map(({ meta: acc }) => {
      const payablesStatus = this.getAccessorialProcessedStatus(
        acc,
        'payables',
      );
      const receivablesStatus = this.getAccessorialProcessedStatus(
        acc,
        'receivables',
      );
      return (
        <TableRow key={acc?.shipment?._id}>
          <TableCell>{t(sluggify(acc?.accessorial_type))}</TableCell>
          <TableCell>
            <ShipmentLink shipment={acc?.shipment} />
          </TableCell>
          <TableCell>{t(sluggify(acc?.shipment?.status))}</TableCell>
          {!shipperSideDeclinedAccessorial && (
            <>
              <TableCell>{acc?.shipment?.carrier_name || '-'}</TableCell>
              <TableCell>
                {acc[`${acc?.payee_type}_name`]
                  ? `${acc[`${acc.payee_type}_name`] ?? ''} (${t(sluggify(acc.payee_type))})`
                  : '-'}
              </TableCell>
              <TableCell>{t(payablesStatus)}</TableCell>
            </>
          )}
          <TableCell>
            {!shipperSideDeclinedAccessorial
              ? t(receivablesStatus)
              : t(sluggify(acc?.shipper_decline_reason_code))}
          </TableCell>
        </TableRow>
      );
    });
  }

  renderMismatchInfo(
    alertType,
    accessorials = [],
    shipperSideDeclinedAccessorial = false,
  ) {
    const { t } = this.props;
    const currentCount = accessorials.length;
    const alertClassName = classnames({
      'w-full': true,
      flex: true,
      'justify-between': true,
      'items-center': true,
      'space-x-2': true,
    });
    if (!accessorials || !currentCount) return <div />;
    return (
      <div className="w-full mx-auto mb-4">
        <Disclosure key="disclosure">
          {({ open }) => {
            return (
              <>
                <Disclosure.Button
                  key="disclosure-button"
                  className="focus:outline-none focus-visible:ring focus-visible:ring-yellow-500 focus-visible:ring-opacity-75 w-full"
                >
                  <Alert type="warning" className="w-full">
                    <div className="flex flex-wrap justify-between w-full">
                      {
                        <div className={alertClassName}>
                          <span className="hidden md:inline">
                            {t(this.getAlertMessage(alertType))}
                          </span>
                          {open && <ChevronDown className="h-6 w-6" />}
                          {!open && <ChevronRight className="h-6 w-6" />}
                        </div>
                      }
                    </div>
                  </Alert>
                </Disclosure.Button>
                <Disclosure.Panel
                  key="disclosure-panel"
                  className="bg-white rounded-md shadow text-sm max-h-80 overflow-y-scroll"
                >
                  {currentCount > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('ACCESSORIAL')}</TableHead>
                          <TableHead>{t('SHIPMENT_ID')}</TableHead>
                          <TableHead>{t('SHIPMENT_STATUS')}</TableHead>
                          {!shipperSideDeclinedAccessorial && (
                            <>
                              <TableHead>{t('SHIPMENT_CARRIER')}</TableHead>
                              <TableHead>{t('ACCESSORIAL_PAYEE')}</TableHead>
                              <TableHead>{t('PAYEE_STATUS')}</TableHead>
                            </>
                          )}
                          <TableHead>{t('PAYER_STATUS')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {this.renderMismatchAlerts(
                          accessorials,
                          shipperSideDeclinedAccessorial,
                        )}
                      </TableBody>
                    </Table>
                  )}
                </Disclosure.Panel>
              </>
            );
          }}
        </Disclosure>
      </div>
    );
  }

  render() {
    const { show, t, edit } = this.props;

    const { saveInProgress, shipments, loadingAccessorials } = this.state;

    const buttonsDisabled =
      shipments.length === 0 || saveInProgress || loadingAccessorials;

    const saveDisabled = this.disableSave();
    let carrierMissingInAccessorial = false,
      carrierMismatchInAccessorial = false;
    const carrierSideDeclinedAccessorial = shipments?.filter(
      ({ meta, carrier }) => {
        const carrierSideDeclined = checkWhichSideAccessorialAutoDeclined(
          meta,
          'carrier',
        );
        const carrierMissing = isCarrierMissingInAccessorial(meta);
        const carrierMismatch =
          meta?.payee_type === 'carrier' &&
          (!!carrier || !!meta?.carrier) &&
          carrier !== meta?.carrier;
        if (carrierMissing) carrierMissingInAccessorial = true;
        if (carrierMismatch) carrierMismatchInAccessorial = true;
        return carrierSideDeclined || carrierMissing || carrierMismatch;
      },
    );

    const carrierErrorTitle = getCarrierAccordionTitle(
      carrierMissingInAccessorial,
      carrierMismatchInAccessorial,
    );
    const shipperSideDeclinedAccessorial = shipments.filter(({ meta }) =>
      checkWhichSideAccessorialAutoDeclined(meta, 'shipper'),
    );
    return (
      <Dialog
        open={show}
        onOpenChange={(open) => {
          if (!open) this.handleClose();
        }}
      >
        <DialogContent
          className="!w-auto !max-w-[95vw] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <DialogTitle>
              {edit
                ? t('EDIT_ACCESSORIAL', { count: 3 })
                : t('ADD_ACCESSORIAL', { count: 3 })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-grey-50 px-6 py-4">
            {this.renderShipmentsLimitErrorInfo()}
            {this.renderProTip()}
            {!!(edit && carrierSideDeclinedAccessorial?.length) &&
              this.renderMismatchInfo(
                carrierErrorTitle,
                carrierSideDeclinedAccessorial,
              )}
            {!!(edit && shipperSideDeclinedAccessorial?.length) &&
              this.renderMismatchInfo(
                'auto_applied_shipper_change_edit',
                shipperSideDeclinedAccessorial,
                true,
              )}
            <div className="bg-white shadow rounded-md mt-4 p-4 overflow-x-auto">
              {this.renderErrors()}
              <Table className="text-sm [&_th]:h-auto [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-1 [&_td]:align-middle">
                {this.renderColumnHeaders()}
                {this.renderRows()}
              </Table>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 sm:justify-end">
            <div className="flex items-start justify-end w-full gap-2">
              <div className="w-1/2 status-wrapper">
                {this.renderStatuses()}
              </div>
              <div className="form-actions flex gap-2">
                <Button
                  variant="outline"
                  disabled={buttonsDisabled}
                  onClick={this.handleClearAll}
                >
                  {titleize(t('CLEAR_ALL'))}
                </Button>
                <Button disabled={saveDisabled} onClick={this.handleSave}>
                  {this.state.saveInProgress && (
                    <span className="flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>{titleize(t('SAVING'))}...</span>
                    </span>
                  )}
                  {this.state.loadingShipments && (
                    <span className="flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>{titleize(t('LOADING'))}</span>
                    </span>
                  )}
                  {!this.state.saveInProgress &&
                    !this.state.loadingShipments && (
                      <span>{titleize(t('SAVE'))}</span>
                    )}
                </Button>
                <Button
                  variant="secondary"
                  disabled={this.state.saveInProgress}
                  onClick={this.handleClose}
                >
                  {titleize(t('CLOSE'))}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

function compareShipments(currentShipments, nextShipments) {
  const shipmentMap = {};

  currentShipments.forEach((job, index) => {
    shipmentMap[job._id] = {
      index,
      exists: false,
    };
  });

  let hasAdded = false;
  let hasUpdated = false;

  const shipments = nextShipments.map((job) => {
    let updated = true;
    if (shipmentMap[job._id]) {
      const index = shipmentMap[job._id].index;
      updated = !isEqual(job, currentShipments[index]);
      shipmentMap[job._id].exists = true;
      if (updated) {
        hasUpdated = true;
      }
    } else {
      hasAdded = true;
    }
    return {
      ...job,
      updated,
    };
  });

  const hasRemoved =
    Object.values(shipmentMap).find((s) => !s.exists) !== undefined;
  const modified = hasAdded || hasUpdated || hasRemoved;

  return {
    modified,
    shipments,
  };
}
function carrierMismatchCheck(accesorial) {
  const { shipment, carrier, payee_type } = accesorial;
  const shipmentCarrier = shipment?.carrier?._id || shipment?.carrier;
  const carrierAssociatedWithAccessorial = carrier?._id || carrier;
  return (
    payee_type === 'carrier' &&
    carrierAssociatedWithAccessorial !== shipmentCarrier
  );
}

function mapShipmentForEdit(accessorial, accessorialList) {
  const shipment = accessorial.shipment;
  const rawEquipmentList = shipment.stops.reduce((runningList, stop) => {
    const { equipments: stopEquipments } = stop;
    return runningList.concat(...stopEquipments);
  }, []);
  const equipments = uniqBy(rawEquipmentList, (e) => e._id);
  const selectedAccessorial = accessorialList.find(
    (acc) =>
      acc.slug === accessorial.accessorial_type &&
      acc.charge_code === accessorial.charge_code,
  );
  const required_fields = Object.keys(REQUIRED_FIELDS_MAP).filter(
    (field) => selectedAccessorial && selectedAccessorial[field],
  );
  const isVendorPaid = !!isVendorChargePaid(
    accessorial?._id,
    shipment?.vendor_payables,
  );

  const {
    _id,
    shipment_id,
    shipper,
    carrier,
    order_type,
    order_oid: orderId,
    currency,
    master_account,
    market,
    payables,
    receivables,
    status,
    vendor_payables,
  } = shipment;
  return {
    _id,
    shipment_id,
    equipments,
    shipper,
    carrier,
    vendor_payables,
    meta: {
      ...accessorial,
      shipper_cost: accessorial.shipper_rate,
      carrier_cost: accessorial.carrier_rate,
      name: accessorial.message,
      equipment: accessorial.equipment[0],
      errors: {},
      isVendorPaid,
      type: accessorial.accessorial_type,
      notes: accessorial.note,
      required_fields,
      disable_payee:
        mapProcessedAccessorialLedgerItem(accessorial, 'payables') ||
        checkWhichSideAccessorialAutoDeclined(accessorial, 'carrier') ||
        isCarrierMissingInAccessorial(accessorial) ||
        carrierMismatchCheck(accessorial),
      disable_payer:
        mapProcessedAccessorialLedgerItem(accessorial, 'receivables') ||
        checkWhichSideAccessorialAutoDeclined(accessorial, 'shipper'),
    },
    order_type,
    orderId,
    currency,
    master_account,
    market,
    payables,
    receivables,
    status,
  };
}

function mapShipment(shipment) {
  const rawEquipmentList = shipment.stops.reduce((runningList, stop) => {
    const { equipments: stopEquipments } = stop;
    return runningList.concat(...stopEquipments);
  }, []);
  const equipments = uniqBy(rawEquipmentList, (e) => e._id);
  const {
    _id,
    shipment_id,
    shipper,
    carrier,
    order_type,
    order_oid: orderId,
    currency,
    master_account,
    market,
    payables,
    receivables,
    status,
    vendor_payables,
    timeline,
  } = shipment;

  return {
    _id,
    shipment_id,
    shipper,
    carrier,
    equipments,
    meta: getDefaultMeta(equipments),
    order_type,
    orderId,
    currency,
    master_account,
    market,
    payables,
    receivables,
    status,
    vendor_payables,
    timeline,
  };
}

function getDefaultMeta(equipments = []) {
  let equipment;

  if (equipments.length === 1) {
    equipment = equipments[0]._id;
  }
  return {
    name: '',
    type: '',
    charge_code: '',
    equipment: equipment || '',
    shipper_cost: '',
    carrier_cost: '',
    shipper_unit_qty: 1,
    carrier_unit_qty: 1,
    notes: '',
    applyAllDisabled: true,
    errors: {},
    isValid: false,
    order_type: '',
    hasCarrierRate: false,
    hasShipperRate: false,
    ratesRequestPending: false,
    payee_type: 'carrier',
    disable_payee: false,
    disable_payer: false,
  };
}

function validateJob(job, t, accessorials = []) {
  const {
    meta: {
      name,
      type,
      equipment,
      shipper_unit_qty,
      shipper_cost,
      carrier_unit_qty,
      required_fields,
      begin_datetime,
      end_datetime,
      location,
      vendor,
      description,
      vendor_enabled,
      payee_type,
    },
    order_type,
    carrier,
  } = job;

  const errors = {};

  if (order_type !== 'export' && !equipment) {
    errors.equipment = t('SELECT_EQUIPMENT_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  if (payee_type === 'vendor' && !vendor) {
    errors.vendor = t('SET_VENDOR_WITH_PAYEE_TYPE', {
      shipmentID: job.shipment_id,
    });
  }

  if ((!carrier || vendor_enabled) && payee_type !== 'vendor') {
    errors.payee_type = t('UPDATE_ACCESSORIAL_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  if (!type) {
    errors.type = t('SET_ACCESSORIAL_TYPE_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  if (!name) {
    errors.name = t('SET_ACCESSORIAL_NAME_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  if (!shipper_unit_qty && !carrier_unit_qty) {
    errors.shipper_unit_qty = t('SHIPPER_UNITS_AMOUNT_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
    errors.carrier_unit_qty = t('CARRIER_UNITS_AMOUNT_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  let parsedShipperCost = parseFloat(shipper_cost);
  if (isNil(parsedShipperCost) || parsedShipperCost < 0) {
    errors.shipper_cost = t('PAYER_RATE_GREATER_THAN_ZERO_WITH_VALUE', {
      shipmentID: job.shipment_id,
    });
  }

  if (type && type !== 'wait_time') {
    if (!parseFloat(shipper_unit_qty) && !parseFloat(carrier_unit_qty)) {
      errors.shipper_unit_qty = t('INTEGER_SHIPPER_UNITS_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
      errors.carrier_unit_qty = t('INTEGER_CARRIER_UNITS_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
  }

  if (type && type === 'wait_time') {
    if (!parseFloat(shipper_unit_qty)) {
      errors.shipper_unit_qty = t('DECIMAL_SHIPPER_UNITS_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }

    if (!parseFloat(carrier_unit_qty)) {
      errors.carrier_unit_qty = t('DECIMAL_CARRIER_UNITS_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
  }

  if (required_fields && required_fields.length) {
    if (required_fields.includes('begin_date_required') && !begin_datetime) {
      errors.begin_date_required = t('BEGIN_TIME_REQUIRED_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
    if (required_fields.includes('end_date_required') && !end_datetime) {
      errors.end_date_required = t('END_TIME_REQUIRED_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
    if (required_fields.includes('location_required') && !location) {
      errors.location_required = t('LOCATION_REQUIRED_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
    if (required_fields.includes('description_required') && !description) {
      errors.description_required = t('DESCRIPTION_REQUIRED_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
    if (
      required_fields.includes('end_date_required') &&
      required_fields.includes('begin_date_required') &&
      end_datetime &&
      begin_datetime &&
      (moment(begin_datetime).isAfter(end_datetime) ||
        moment(begin_datetime).isSame(end_datetime))
    ) {
      errors.date_inconsistency = t('TIME_NOT_SAME_OR_BEFORE_WITH_VALUE', {
        shipmentID: job.shipment_id,
      });
    }
  }
  // When the accessorial auto-calculates units from Begin/End, those dates
  // drive the read-only Units field, so they must be present and ordered —
  // independent of the required_fields flags.
  const selectedAccessorial = accessorials.find((a) => a.slug === type);
  if (
    selectedAccessorial?.auto_calculate_units &&
    selectedAccessorial?.calc_method
  ) {
    const appliesTo = getAppliesToFields(selectedAccessorial.applies_to);
    if (appliesTo.shipper || appliesTo.carrier) {
      if (!begin_datetime) {
        errors.begin_date_required = t('BEGIN_TIME_REQUIRED_WITH_VALUE', {
          shipmentID: job.shipment_id,
        });
      }
      if (!end_datetime) {
        errors.end_date_required = t('END_TIME_REQUIRED_WITH_VALUE', {
          shipmentID: job.shipment_id,
        });
      }
      if (
        begin_datetime &&
        end_datetime &&
        moment(end_datetime).isBefore(begin_datetime)
      ) {
        errors.date_inconsistency = t('TIME_NOT_SAME_OR_BEFORE_WITH_VALUE', {
          shipmentID: job.shipment_id,
        });
      }
    }
  }

  const isValid = Object.keys(errors).length === 0;
  return {
    ...job,
    meta: {
      ...job.meta,
      errors,
      isValid,
    },
  };
}

function getEquipment(equipmentId, equipments) {
  const equipment = equipments.find((eq) => eq._id.toString() === equipmentId);
  if (equipment) {
    return [equipment];
  }
  return equipments;
}

function convertToAccessorialRequest(
  shipment,
  accessorials,
  customerApprovalAccessorials,
  customerApprovalProvided,
) {
  const { meta, equipments, status } = shipment;
  const accessorial = accessorials.find(
    (accessorial) => accessorial.name === meta.name,
  );

  // For auto-calculate accessorials the Units field is system-managed. Derive
  // the units from Begin/End at submit instead of trusting the (read-only,
  // possibly stale) meta value, so the FE never sends user-entered units for an
  // auto-managed side.
  let carrierUnitQty = parseFloat(meta.carrier_unit_qty) || 1;
  let shipperUnitQty = parseFloat(meta.shipper_unit_qty) || 1;
  let metaForRequest = meta;
  if (accessorial?.auto_calculate_units && accessorial?.calc_method) {
    const appliesTo = getAppliesToFields(accessorial.applies_to);
    const calc = getAutoUnitCalculation(accessorial, {
      beginDate: meta.begin_datetime,
      endDate: meta.end_datetime,
    });
    if (calc.units !== null) {
      if (appliesTo.carrier) carrierUnitQty = calc.units;
      if (appliesTo.shipper) shipperUnitQty = calc.units;
      metaForRequest = {
        ...meta,
        ...(appliesTo.carrier ? { carrier_unit_qty: carrierUnitQty } : {}),
        ...(appliesTo.shipper ? { shipper_unit_qty: shipperUnitQty } : {}),
      };
    }
  }

  const request = {
    type: 'accessorial',
    exception_type: 'Accessorial Request',
    carrier_status: 'pending-admin',
    shipper_status: 'pending-shipper',
    shipment: shipment._id,
    shipper: shipment.shipper
      ? shipment.shipper._id || shipment.shipper
      : shipment.shipper,
    carrier: shipment.carrier
      ? shipment.carrier._id || shipment.carrier
      : shipment.carrier,
    accessorial_type: meta.type,
    message: meta.name,
    charge_code: meta.charge_code,
    shipment_id: shipment.shipment_id,
    shipper_name: shipment.shipper_name,
    carrier_name: shipment.carrier_name,
    begin_datetime: meta.begin_datetime,
    end_datetime: meta.end_datetime,
    vendor: meta.vendor,
    vendor_name: meta.vendor_name,
    payee_type: meta.payee_type,
    location: meta.location,
    description: meta.description,
    carrier_rate: parseFloat(meta.carrier_cost),
    carrier_unit_qty: carrierUnitQty,
    shipper_rate: parseFloat(meta.shipper_cost),
    shipper_unit_qty: shipperUnitQty,
    equipment: meta?.equipment
      ? getEquipment(meta.equipment, equipments)
      : equipments,
    note: meta.notes,
    shipper_note: meta.notes,
    carrier_note: meta.notes,
    meta: metaForRequest,
    days_before_auto_decline: accessorial.days_before_auto_decline,
    anchor_event: accessorial.anchor_event,
    master_shipper: shipment.master_account ? shipment.master_account.id : null,
    status,
    rate_source: meta.rate_source || 'manual',
    receivable_rate_source: meta.receivable_rate_source || 'manual',
    payable_rate_source: meta.payable_rate_source || 'manual',
  };
  if (customerApprovalAccessorials.includes(meta.type)) {
    request.customer_approval_provided = customerApprovalProvided;
  }
  if (shipment?.timeline?.job_closed?.at)
    request.job_closed_date = shipment.timeline.job_closed.at;
  return request;
}

export default withTranslation()(BulkAddAccessorialsModal);
